import { motion } from "framer-motion";

const phases = [
  {
    days: "Days 1–30",
    label: "Get the customer right",
    summary:
      "Turn the analysis into one clear customer, one buying moment, and one message your team can use.",
    actions: [
      "Align the team around your rich customer avatar",
      "Tighten positioning, offers, and the language on your highest-value pages",
      "Fix the biggest gaps between what your best customers want and what your marketing says",
    ],
  },
  {
    days: "Days 31–60",
    label: "Go capture demand",
    summary:
      "Put the message in front of more qualified people through the channels your customer data supports.",
    actions: [
      "Build campaigns around the strongest buying triggers",
      "Prioritize the acquisition channels most likely to reach this customer",
      "Create a focused test plan for paid, lifecycle, content, and sales outreach",
    ],
  },
  {
    days: "Days 61–90",
    label: "Scale what works",
    summary:
      "Use early results to decide what deserves more budget, more creative, and more attention.",
    actions: [
      "Move resources toward the messages and channels producing real signal",
      "Build retention and expansion plays for the customers you already have",
      "Set a simple scorecard so the next decision is based on evidence",
    ],
  },
];

export default function Newsletter() {
  return (
    <section
      id="action-plan"
      data-testid="action-plan-section"
      className="relative py-12 lg:py-16 border-t border-white/[0.06] overflow-hidden scroll-mt-24"
    >
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-[920px] mb-10 lg:mb-12"
        >
          <div className="font-mono-label text-[#D4FF4A]/70 mb-4">
            Your 30 / 60 / 90-day growth plan
          </div>
          <h2
            data-testid="action-plan-heading"
            className="font-display text-white text-[40px] lg:text-[64px] font-semibold leading-[1.02] tracking-[-0.03em] mb-5"
          >
            We find the signal. Then we tell you what to do with it.
          </h2>
          <p className="text-lg text-[#A1A1AA] leading-[1.6] max-w-[790px]">
            A dashboard can show you what happened. Your written action plan turns the findings into priorities your marketing team can execute over the next 90 days.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {phases.map((phase, index) => (
            <motion.article
              key={phase.days}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.65, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-[#0A0A0F] border border-white/[0.08] rounded-2xl p-6 lg:p-7 overflow-hidden"
            >
              <div className="absolute inset-x-0 top-0 h-[2px] bg-[#D4FF4A] shadow-[0_0_14px_rgba(212,255,74,0.45)]" />
              <div className="font-mono text-[#D4FF4A] text-[11px] tracking-[0.12em] uppercase mb-4">
                {phase.days}
              </div>
              <h3 className="font-display text-white text-[25px] font-semibold leading-tight mb-3">
                {phase.label}
              </h3>
              <p className="text-[#A1A1AA] text-[14px] leading-[1.6] mb-6 min-h-[68px]">
                {phase.summary}
              </p>
              <div className="space-y-3 pt-5 border-t border-white/[0.06]">
                {phase.actions.map((action) => (
                  <div key={action} className="flex items-start gap-2.5">
                    <span className="text-[#D4FF4A] font-mono text-[12px] mt-0.5">→</span>
                    <p className="text-[#D4D4D8] text-[13px] leading-[1.55]">{action}</p>
                  </div>
                ))}
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-6 p-5 lg:px-7 rounded-xl bg-[#D4FF4A]/[0.04] border border-[#D4FF4A]/20 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-white text-[14px] font-medium">
            Built from your customer data, your business, and the opportunities we uncover together.
          </p>
          <span className="font-mono text-[#D4FF4A] text-[11px] tracking-[0.08em] uppercase whitespace-nowrap">
            Included in the $999 beta
          </span>
        </div>
      </div>
    </section>
  );
}
